import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Progress,
  Select,
  Spinner,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { MdContentCopy } from 'react-icons/md';
import { MdEditNote } from 'react-icons/md';
import { FiAlertCircle } from 'react-icons/fi';
import {
  ConnectionType,
  ConnectorStatus,
  SteeringStatus,
  useGetMyCustomAgentsQuery,
  useTriggerInjectionAnalysisMutation,
  useSteeringRunQuery,
} from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import { getSimilarityStyle } from './ProjectTabViewFaq';
import { humanizeTokens } from '../../utils/tokenEstimate';

// Mirrors the worker's STEERING_CANDIDATE_COUNT; ~output tokens for one plan-mode task plan.
const STEER_CANDIDATES = 4;
const PER_TASK_OUTPUT_TOKENS = 1500;

interface InjectionOptimizerProps {
  agentId?: string;
  sessionId?: string | null;
  taskCount?: number;
  // The watch's completed versions — one is the target state to steer toward.
  timelineProjects?: Array<{ id: string; dateCreated: string }>;
}

const TERMINAL: SteeringStatus[] = [SteeringStatus.Completed, SteeringStatus.Failed];

export const InjectionOptimizer = ({
  agentId,
  sessionId,
  taskCount = 0,
  timelineProjects = [],
}: InjectionOptimizerProps) => {
  const toast = useToast();

  // Candidate target versions (newest first). The most recent IS the current state, so it
  // makes a poor "steer back toward" target — offer the earlier versions.
  const targets = useMemo(
    () =>
      [...timelineProjects]
        .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
        .slice(1),
    [timelineProjects],
  );
  const [targetId, setTargetId] = useState<string>('');
  useEffect(() => {
    if (!targetId && targets.length > 0) setTargetId(targets[0].id);
  }, [targets, targetId]);

  // Approx monitored-agent output tokens: (baseline + K injections) × tasks × per-task plan.
  const planSets = STEER_CANDIDATES + 1;
  const estTokens = taskCount > 0 ? planSets * taskCount * PER_TASK_OUTPUT_TOKENS : 0;

  const { data: agentsData } = useGetMyCustomAgentsQuery();
  const agent = useMemo(
    () => (agentsData?.getMyCustomAgents ?? []).find((a) => a.id === agentId),
    [agentsData, agentId],
  );
  const isConnector = agent?.connectionType === ConnectionType.Connector;
  const isOnline = agent?.connectorStatus === ConnectorStatus.Online;

  const [runId, setRunId] = useState<string | undefined>();
  const [trigger, { loading: triggering }] = useTriggerInjectionAnalysisMutation();
  const { data: runData, stopPolling } = useSteeringRunQuery({
    variables: { id: runId as string },
    skip: !runId,
    pollInterval: 3000,
    fetchPolicy: 'network-only',
  });
  const run = runData?.steeringRun;
  useEffect(() => {
    if (run && TERMINAL.includes(run.status)) stopPolling();
  }, [run, stopPolling]);
  const isRunning = !!runId && (!run || !TERMINAL.includes(run.status));

  const onAnalyze = async () => {
    if (!agentId || !sessionId || !targetId) return;
    setRunId(undefined);
    try {
      const res = await trigger({
        variables: { input: { agentId, sessionId, referenceVersionId: targetId } },
      });
      const id = res.data?.triggerInjectionAnalysis.id;
      if (id) setRunId(id);
    } catch (e) {
      toast({ title: 'Could not start analysis', description: String(e), status: 'error', duration: 4000 });
    }
  };

  const copyMessage = (message: string) => {
    navigator.clipboard.writeText(message);
    toast({ title: 'Copied — paste it into your session', description: message, status: 'success', duration: 2500 });
  };

  // Baseline similarity = the current, un-nudged plan's closeness to the target (the "do
  // nothing" reference). Lift for each message is its similarity minus the baseline.
  const baselineSim = useMemo(() => {
    const b = run?.results?.candidates?.find((c) => c.isBaseline);
    return b?.similarity ?? null;
  }, [run]);

  // --- Gating ---
  if (!sessionId) {
    return (
      <EmptyState
        title="This watch isn't pinned to a session"
        body="Injection steering runs against the Claude Code session this watch monitors. This watch was created without one."
      />
    );
  }
  if (!isConnector) {
    return (
      <EmptyState
        title="Not a Claude Code agent"
        body="Injection steering only applies to connector-based coding agents (Claude Code)."
      />
    );
  }
  if (targets.length === 0) {
    return (
      <EmptyState
        title="Need an earlier version to steer toward"
        body="This tool pulls the agent's current behavior back toward an earlier watch version's plans. Let the watch run a few more check-ins, then come back."
      />
    );
  }

  return (
    <Flex width="full" flexDirection="column" maxWidth="900px" mx="auto" gap={4}>
      {/* Header */}
      <Flex
        gap={3}
        p={4}
        bg="white"
        border="1px solid"
        borderColor={HelivanColor.border.primary}
        borderRadius="12px"
        align="center"
        justify="space-between"
        flexWrap="wrap"
      >
        <HStack spacing={3} minW={0}>
          <Flex w="34px" h="34px" borderRadius="8px" bg={HelivanColor.hover.lightBlue} align="center" justify="center" flexShrink={0}>
            <MdEditNote size={18} color={HelivanColor.text.helivanBlue} />
          </Flex>
          <VStack align="start" spacing={0} minW={0}>
            <HStack spacing={2}>
              <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark}>
                Recommend an injection message
              </Text>
              <Badge colorScheme="purple" fontSize="10px">
                Experimental
              </Badge>
            </HStack>
            <Text fontSize="12px" color={HelivanColor.text.blueMid} noOfLines={1}>
              For this watch's session · {agent?.name ?? 'agent'}
            </Text>
          </VStack>
        </HStack>
        <VStack align={{ base: 'stretch', sm: 'end' }} spacing={1}>
          <Button
            size="sm"
            colorScheme="blue"
            bg={HelivanColor.text.helivanBlue}
            width={{ base: '100%', sm: 'auto' }}
            isDisabled={!isOnline || !targetId || triggering || isRunning}
            isLoading={triggering}
            onClick={onAnalyze}
          >
            Find a message
          </Button>
          {estTokens > 0 && (
            <Text fontSize="11px" color={HelivanColor.text.blueMid}>
              ≈ {humanizeTokens(estTokens)} output tokens · {planSets} plan runs (approx, your agent)
            </Text>
          )}
        </VStack>
      </Flex>

      {/* Target selector */}
      <VStack
        align="stretch"
        spacing={2}
        px={4}
        py={3}
        bg="white"
        border="1px solid"
        borderColor={HelivanColor.border.primary}
        borderRadius="12px"
      >
        <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
          <Text fontSize="13px" fontWeight={500} color={HelivanColor.text.blueDark}>
            Steer back toward
          </Text>
          <Select
            size="sm"
            width={{ base: '100%', sm: '280px' }}
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            isDisabled={isRunning || triggering}
          >
            {targets.map((v) => (
              <option key={v.id} value={v.id}>
                Version from {new Date(v.dateCreated).toLocaleString()}
              </option>
            ))}
          </Select>
        </Flex>
        <Text fontSize="12px" color={HelivanColor.text.blueMid}>
          We measure each candidate by resuming your live session, adding the message, and re-planning the
          watch's tasks — then rank by how close that gets to this target version's plans. We only suggest
          the message; you paste it in yourself.
        </Text>
      </VStack>

      {agent && !isOnline && (
        <InlineHint text={`"${agent.name}" is offline — start its connector to run this tool.`} />
      )}

      {/* Run state */}
      {isRunning && (
        <Flex
          direction="column"
          align="center"
          justify="center"
          py={12}
          gap={3}
          bg="white"
          border="1px solid"
          borderColor={HelivanColor.border.primary}
          borderRadius="12px"
          px={6}
        >
          {!run?.progressTotal ? (
            <Spinner color={HelivanColor.text.helivanBlue} />
          ) : (
            <Box width="full" maxWidth="420px">
              <Progress
                value={(run.progressDone ?? 0) / run.progressTotal * 100}
                size="sm"
                borderRadius="full"
                colorScheme="blue"
                hasStripe
                isAnimated
              />
            </Box>
          )}
          <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark}>
            {run?.progressLabel || 'Trying injection messages…'}
          </Text>
          <Text fontSize="13px" color={HelivanColor.text.blueMid} textAlign="center" maxWidth="420px">
            {run?.progressTotal
              ? `Step ${run.progressDone ?? 0} of ${run.progressTotal} · re-planning the watch's tasks under each message.`
              : 'Resuming your session under each candidate and measuring how close it pulls the plan to the target.'}
          </Text>
        </Flex>
      )}

      {run?.status === SteeringStatus.Failed && (
        <EmptyState title="Analysis failed" body={run.errorMessage || 'Something went wrong. Please try again.'} />
      )}

      {/* Results */}
      {run?.status === SteeringStatus.Completed && run.results && (
        <VStack align="stretch" spacing={3}>
          <Text fontSize="13px" color={HelivanColor.text.blueMid}>
            Ranked by how close each message pulls the agent's plan to the target version. "Lift" is the gain
            over doing nothing (the baseline). Copy the top one and paste it into your live session.
          </Text>
          {run.results.candidates
            .filter((c) => !c.isBaseline)
            .map((c, idx) => {
              const sim = getSimilarityStyle(c.similarity);
              const isRecommended = idx === 0 && (c.similarity ?? -1) >= 0;
              const lift = c.similarity != null && baselineSim != null ? c.similarity - baselineSim : null;
              return (
                <Box
                  key={idx}
                  bg="white"
                  border="1px solid"
                  borderColor={isRecommended ? HelivanColor.text.helivanBlue : HelivanColor.border.primary}
                  borderLeft="4px solid"
                  borderLeftColor={sim.border}
                  borderRadius="12px"
                  p={4}
                  boxShadow={isRecommended ? '0 1px 6px rgba(0,0,0,0.08)' : 'none'}
                >
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between" align="center">
                      <HStack spacing={2}>
                        {isRecommended && (
                          <Badge colorScheme="blue" fontSize="10px">
                            Recommended
                          </Badge>
                        )}
                        {lift != null && (
                          <Badge colorScheme={lift > 0 ? 'green' : 'gray'} fontSize="10px">
                            {lift >= 0 ? '+' : ''}
                            {(lift * 100).toFixed(0)} pts vs. baseline
                          </Badge>
                        )}
                      </HStack>
                      <Button
                        size="xs"
                        variant="outline"
                        leftIcon={<MdContentCopy size={13} />}
                        onClick={() => copyMessage(c.message)}
                        flexShrink={0}
                      >
                        Copy
                      </Button>
                    </HStack>
                    {/* The raw injection message — paste into the live session */}
                    <Box
                      as="pre"
                      onClick={() => copyMessage(c.message)}
                      cursor="pointer"
                      title="Click to copy"
                      width="full"
                      bg="#0f172a"
                      color="#e2e8f0"
                      fontFamily="mono"
                      fontSize="13px"
                      lineHeight="1.6"
                      px={3}
                      py={2.5}
                      borderRadius="8px"
                      whiteSpace="pre-wrap"
                      overflowWrap="anywhere"
                    >
                      {c.message}
                    </Box>
                    {/* Similarity bar */}
                    <Flex align="center" gap={2} width="full">
                      <Box flex={1} h="6px" bg={HelivanColor.background.secondary} borderRadius="full" overflow="hidden">
                        <Box
                          h="full"
                          width={`${Math.round((c.similarity ?? 0) * 100)}%`}
                          bg={sim.border}
                          borderRadius="full"
                        />
                      </Box>
                      <Tooltip label="Cosine similarity of the nudged plan to the target version's plan" hasArrow placement="top">
                        <Badge colorScheme={sim.scheme} fontSize="11px" minWidth="84px" textAlign="center">
                          {sim.label}
                        </Badge>
                      </Tooltip>
                    </Flex>
                  </VStack>
                </Box>
              );
            })}
        </VStack>
      )}
    </Flex>
  );
};

const EmptyState = ({ title, body }: { title: string; body: string }) => (
  <Flex
    direction="column"
    align="center"
    justify="center"
    textAlign="center"
    py={16}
    gap={2}
    bg="white"
    border="1px solid"
    borderColor={HelivanColor.border.primary}
    borderRadius="12px"
  >
    <Icon as={FiAlertCircle} boxSize="26px" color={HelivanColor.text.blueMid} />
    <Text fontSize="16px" fontWeight={700} color={HelivanColor.text.blueDark}>
      {title}
    </Text>
    <Text fontSize="14px" color={HelivanColor.text.blueMid} maxWidth="440px" lineHeight="1.6">
      {body}
    </Text>
  </Flex>
);

const InlineHint = ({ text }: { text: string }) => (
  <HStack
    spacing={2}
    px={3}
    py={2}
    bg={HelivanColor.background.secondary}
    border="1px solid"
    borderColor={HelivanColor.border.primary}
    borderRadius="8px"
  >
    <FiAlertCircle size={14} color={HelivanColor.text.blueMid} />
    <Text fontSize="13px" color={HelivanColor.text.blueMid}>
      {text}
    </Text>
  </HStack>
);
